// Audit Hasil Baca AI (Owner/Admin): hanya menampilkan data tersimpan — tidak menjalankan AI, tanpa SKU.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Eye, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-mockup-read`;
const NA = "Data tidak tersedia";
const SL = { pendek: "Pendek", panjang: "Panjang", tanpa_lengan: "Tanpa Lengan", tidak_berlaku: "Tidak Berlaku" };
const ST = { done: "Berhasil", failed: "Gagal", skipped: "Dilewati", queued: "Antre", processing: "Diproses" };
const fmt = (s) => { if (!s || s === NA) return NA; const d = new Date(s); return isNaN(d) ? s : d.toLocaleString("id-ID"); };
const sum = (rows) => (rows || []).reduce((n, r) => n + (Number(r.qty) || 0), 0);

function Rows({ rows, tid }) {
  if (!rows?.length) return <div className="muted small">{NA}</div>;
  return <table className="whn-table" data-testid={tid}><thead><tr><th>Produk</th><th>Warna</th><th>Lengan</th><th>Ukuran</th><th>Jumlah</th><th>Keterangan</th></tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i}><td>{r.product || "?"}</td><td>{r.color === "tidak_dapat_dipastikan" ? "?" : (r.color || "?")}</td>
      <td>{SL[r.sleeve] || r.sleeve || "?"}</td><td>{r.size || "?"}</td><td>{r.qty ?? "?"}</td>
      <td className="small">{[r.status && r.status !== "OK" ? r.status : "", ...(r.why || [])].filter(Boolean).join(" · ") || "—"}</td></tr>)}</tbody></table>;
}

function Stage({ n, title, children }) {
  return <section className="aia-stage" data-testid={`ai-audit-stage-${n}`}><h4>{n}. {title}</h4>{children}</section>;
}

function Img({ rid }) {
  const [src, setSrc] = useState(""); const [err, setErr] = useState("");
  useEffect(() => {
    let u = "";
    axios.get(`${API}/${rid}/mockup`, { withCredentials: true, responseType: "blob" })
      .then(r => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => setErr("Mockup tidak dapat dibuka"));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [rid]);
  return src ? <img src={src} alt="mockup yang dianalisis" className="aia-img" data-testid="ai-audit-image"/> : <div className="muted small">{err || "Memuat mockup…"}</div>;
}

export function AiAuditModal({ rid, onClose }) {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/audit/${rid}`, { withCredentials: true }).then(r => setD(r.data))
      .catch(e => setErr(e?.response?.status === 403 ? "Hanya Owner/Admin" : (e?.response?.data?.detail || "Gagal memuat")));
  }, [rid]);
  const norm = d?.normalized?.rows, fin = d?.final?.rows;
  return <div className="aia-back" onClick={onClose} data-testid="ai-audit-modal">
    <div className="aia-panel" onClick={e => e.stopPropagation()}>
      <div className="aia-head"><b>Hasil Baca AI{d ? ` · ${d.order_number || "—"}` : ""}</b>
        <button type="button" aria-label="Tutup" onClick={onClose} data-testid="ai-audit-close"><X size={18}/></button></div>
      {!d ? <div className="muted">{err || "Memuat…"}</div> : <div className="aia-body">
        <div className="aia-meta" data-testid="ai-audit-meta">
          <span>Status: <b>{ST[d.status] || d.status}</b></span><span>Model: <b>{d.model}</b></span>
          <span>Mulai: {fmt(d.ai_started_at)}</span><span>Selesai: {fmt(d.finished_at)}</span>
          {d.ai_s != null && <span>Durasi AI: {d.ai_s} dtk</span>}
          <span>Jumlah di pesanan: <b data-testid="ai-audit-order-qty">{d.order_qty ?? NA}</b></span>
          <span>Terbaca AI: <b data-testid="ai-audit-ai-qty">{norm ? `${sum(norm)} pcs` : NA}</b></span>
        </div>
        {(d.reason || (d.error_type && d.error_type !== NA)) && <div className="aia-err" data-testid="ai-audit-error">
          {d.reason}{d.error_type && d.error_type !== NA ? ` · teknis: ${d.error_type}` : ""}</div>}
        <Img rid={d.id}/>
        <Stage n={1} title="Respons mentah AI"><pre className="aia-raw" data-testid="ai-audit-raw">{d.raw_response || NA}</pre></Stage>
        <Stage n={2} title="Setelah diproses aplikasi (normalisasi)">
          <Rows rows={norm} tid="ai-audit-normalized"/>
          {d.normalized?.warnings?.length > 0 && <div className="small aia-warn">Diragukan: {d.normalized.warnings.join(" · ")}</div>}
        </Stage>
        <Stage n={3} title="Data akhir untuk ACC Barang">
          <Rows rows={fin} tid="ai-audit-final"/>
          <div className="small">Status tampilan ACC: <b>{d.final?.status || NA}</b>{d.final?.rows ? ` · total ${sum(fin)} pcs` : ""}</div>
          <div className="small" data-testid="ai-audit-cut">Catatan potong stok: {d.acc_stock_cut ? `${d.acc_stock_cut.label || d.acc_stock_cut.status}${d.acc_stock_cut.doc_number ? ` (${d.acc_stock_cut.doc_number})` : ""}` : NA}</div>
        </Stage>
      </div>}
    </div>
  </div>;
}

export function AiAuditButton({ rid, tid }) {
  const [open, setOpen] = useState(false);
  if (!rid) return null;
  return <>
    <button type="button" className="soft-btn small-btn" onClick={e => { e.stopPropagation(); setOpen(true); }} data-testid={`ai-audit-open-${tid || rid}`}><Eye size={12}/> Lihat Hasil AI</button>
    {open && <AiAuditModal rid={rid} onClose={() => setOpen(false)}/>}
  </>;
}

export function AiFailureAudit() {
  const [d, setD] = useState(null); const [err, setErr] = useState(""); const [recent, setRecent] = useState([]);
  useEffect(() => {
    axios.get(`${API}/audit/failures`, { withCredentials: true }).then(r => setD(r.data)).catch(() => setErr("Gagal memuat audit"));
    axios.get(`${API}/audit/recent`, { withCredentials: true }).then(r => setRecent(r.data.items || [])).catch(() => {});
  }, []);
  if (err) return <div className="small muted">{err}</div>;
  if (!d) return null;
  return <details className="aia-fail" data-testid="ai-audit-failures">
    <summary><b>Audit Hasil AI</b> · penyebab gagal/dilewati dari log tersimpan ({d.total})</summary>
    {d.groups.map((g, i) => <div key={i} className="aia-group" data-testid={`ai-audit-group-${i}`}>
      <div><b>{g.count}×</b> {ST[g.status] || g.status} — {g.reason}</div>
      <div className="aia-ex">{g.examples.map(x => <span key={x.id}>{x.order_number || "—"} <AiAuditButton rid={x.id} tid={`f-${x.id}`}/></span>)}</div>
    </div>)}
    {recent.length > 0 && <div className="aia-group"><b>30 pembacaan terbaru</b>
      <div className="aia-ex">{recent.map(x => <span key={x.id} data-testid={`ai-audit-recent-${x.id}`}>{x.order_number || "—"} · {ST[x.status] || x.status} <AiAuditButton rid={x.id} tid={`r-${x.id}`}/></span>)}</div></div>}
  </details>;
}
